import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { color, radius, shadow } from "../../styles/theme";

/**
 * Shared modal chrome — every modal in this app previously hand-rolled its
 * own overlay/header/close-button markup with slightly different values.
 * One component now owns that shell (plus entrance animation), so every
 * dialog looks and behaves the same.
 *
 * Rendered through a portal into document.body rather than in place: this
 * modal's `position: fixed` overlay only actually positions against the
 * viewport if nothing between it and the page root has a `transform` (or
 * filter/perspective) — and `.modal-pop`'s own entrance animation sets one
 * (transform: scale(...)), which is non-"none" throughout, including its
 * finished state. So a Modal nested inside another Modal's children (e.g.
 * BusinessAssistantPermissionsModal opening EditPermissionsModal) had its
 * overlay's containing block become the *outer* modal's small box instead
 * of the viewport — cropped/squished into that corner instead of covering
 * the screen. A portal sidesteps this for any future nesting too, not just
 * the case that surfaced it.
 */
const Modal = ({ title, subtitle, onClose, children, footer, maxWidth = 440, busy = false }) => {
  const { t } = useTranslation("common");
  return createPortal(
    <div className="overlay-fade" style={s.overlay} onClick={busy ? undefined : onClose}>
      <div className="modal-pop" style={{ ...s.modal, maxWidth }} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <div style={{ minWidth: 0 }}>
            {title && <h3 style={s.title}>{title}</h3>}
            {subtitle && <p style={s.subtitle}>{subtitle}</p>}
          </div>
          <button type="button" className="btn-ghost" style={s.closeBtn} onClick={onClose} disabled={busy} aria-label={t("modal.close")}>
            <X size={17} strokeWidth={2} />
          </button>
        </div>
        <div style={s.body}>{children}</div>
        {footer && <div style={s.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

const s = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(20, 21, 40, 0.5)", backdropFilter: "blur(2px)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 16,
  },
  modal: {
    width: "100%", background: color.surface, borderRadius: radius.lg,
    boxShadow: shadow.lg, maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "20px 22px 4px" },
  title: { margin: 0, fontSize: 16.5, fontWeight: 700, color: color.textPrimary, letterSpacing: -0.1 },
  subtitle: { margin: "3px 0 0", fontSize: 12.5, color: color.textSecondary },
  closeBtn: { width: 30, height: 30, padding: 0, borderRadius: radius.sm, flexShrink: 0 },
  body: { padding: "16px 22px 22px", overflowY: "auto" },
  footer: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 22px", borderTop: `1px solid ${color.border}` },
};

export default Modal;
